"use client";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import useProductStore, { ProductIdentity } from "@/util/store/useProductStore";
import { PersianNum } from "@/util/tools/PersianNum";
import { ParseUrl, ProductPageTypeAlbum, ProductPageTypeProductIdentity } from "@/view/products/product";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

interface AttributePricesProps {
    data: ProductPageTypeProductIdentity[];
    albums: ProductPageTypeAlbum[];
    attr: ParseUrl
}

export const transformData = (data: ProductPageTypeProductIdentity[]): ProductIdentity[] => {
    return data?.map((item) => ({
        ...item,
        ProdProperties: item.ProdProperties.map((prop) => ({
            Attribute: prop.Attribute,
            Property: {
                ...prop.Property,
                property_main: true, // or some logic to set this correctly
                property_price: true, // or some logic to set this correctly
            },
        })),
        Warehouses: item.Warehouses.map((warehouse) => ({
            ...warehouse,
            warehouse_price: warehouse.warehouse_price ?? 0,
            warehouse_discount: warehouse.warehouse_discount ?? 0,
            warehouse_available: warehouse.warehouse_available ?? false,
            ProductId: warehouse.ProductId,
            ProductIdentityId: warehouse.ProductIdentityId,
        })),
    }));
};

const AttributePrices = ({ data, albums, attr }: AttributePricesProps) => {
    const path = usePathname()
    const router = useRouter()
    const [currentProdIID, setCurrentProdIID] = useState(attr.pi);
    const { checkSellButton, setCheckSellButton } = useProductStore();
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (attr.pi !== checkSellButton?.ProductIdentityId) {
            const transformedData = transformData(data);
            setCheckSellButton(transformedData, attr.pi);
        }
    }, [])

    const DiscountShow = ({
        price,
        discount,
    }: {
        price: number;
        discount: number;
    }) => (
        <div className="flex flex-wrap items-center justify-center">
            <div className="flex line-through items-center border-ebony mx-2">
                <p className="text-[14px] ">{PersianNum(price)}</p>
                <span className="text-[13px]">تومان</span>
            </div>
            <div className="flex items-center justify-center">
                <p className="text-[18px] text-salmon font-semibold">
                    {PersianNum(discount)}
                </p>
                <span className="text-salmon text-[13px] mr-1">
                    تومان
                </span>
            </div>
        </div>
    );

    const PriceShow = ({ prices }: { prices: number }) =>
        prices ? (
            <div className="flex items-center justify-center">
                <p className="text-[18px] text-ebony font-semibold">
                    {PersianNum(prices)}
                </p>
                <span className="text-ebony text-[13px] mr-1">
                    تومان
                </span>
            </div>
        ) : null;


    const handleSelectChange = (attributeId: number, propertyId: number, ProductIdentityId: number) => {
        setLoading(true)
        setCurrentProdIID(ProductIdentityId); // به‌روزرسانی currentProdIID
        const transformedData = transformData(data);
        setCheckSellButton(transformedData, ProductIdentityId);

        // به‌روزرسانی URL پس از انتخاب
        router.replace(`${path}?attri=[${propertyId},${attributeId},${ProductIdentityId}]`);
        setTimeout(() => {
            setLoading(false)
        }, 200);
    };

    const MainAlbum = (id: number) => {
        const album = albums.filter((b) => b.ProductIdentityId === id);
        const albumCheck = album.filter((b) => b.album_image_main === true);
        const mainAlbum = albumCheck[0] ? albumCheck[0] : album[0];
        const img = mainAlbum?.album_image_src;
        const alt = mainAlbum?.album_image_alt;
        return { img, alt };
    };

    const sortedData = [...data].sort((a, b) => {
        // Sorting by warehouse price in descending order (highest price first)
        const priceA = a.Warehouses[0]?.warehouse_price || 0;
        const priceB = b.Warehouses[0]?.warehouse_price || 0;
        return priceB - priceA;
    });

    return (
        <div className="bg-main mt-2 rounded-sm">
            <Table className="w-full">
                <TableHeader>
                    <TableRow>
                        <TableHead className="text-[12px] text-slate-900 font-semibold text-center">
                            انتخاب
                        </TableHead>
                        <TableHead className="text-[12px] text-slate-900 font-semibold text-center">
                            حجم
                        </TableHead>
                        <TableHead className="text-[12px] text-slate-900 font-semibold text-center">
                            قیمت
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ?
                        new Array(sortedData.length).fill("").map((_, ndx) =>
                            <TableRow key={ndx}>
                                <TableCell>
                                    <Skeleton className="h-[45px] w-full my-1" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton className="h-[45px] w-full my-1" />
                                </TableCell>
                                <TableCell>
                                    <Skeleton className="h-[45px] w-full my-1" />
                                </TableCell>
                            </TableRow>
                        )
                        :
                        sortedData.map((i) => (
                            <TableRow
                                key={i.id}
                                style={{ cursor: "pointer" }}
                                role="button"
                                onClick={() => handleSelectChange(
                                    i.ProdProperties[0]?.Attribute.id,
                                    i.ProdProperties[0]?.Property.id,
                                    i.id
                                )}
                                aria-label={`Select ${i.ProdProperties[0]?.Attribute.attribute_name}`}
                            >
                                <TableCell className="text-[12px] font-normal text-center">
                                    <div className="flex items-center w-20 justify-between ">
                                        <div className="h-[50px] w-[50px] relative">
                                            <Image
                                                alt={MainAlbum(i.id)?.alt || 'جزیره عطر'}
                                                src={process.env.NEXT_PUBLIC_IMAGE_URL + MainAlbum(i.id).img}
                                                fill
                                                quality={85}
                                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                                className="rounded-md object-contain aspect-square filter"
                                                style={{
                                                    filter: 'brightness(1) contrast(1.1) saturate(1.1)',
                                                }}
                                            />
                                        </div>
                                        <input
                                            onChange={(e) => console.log(e.target.checked)}
                                            className="accent-salmon"
                                            type="radio"
                                            checked={i.id === currentProdIID}
                                            aria-checked={i.id === currentProdIID}
                                            aria-label={`Select ${i.ProdProperties[0]?.Attribute.attribute_name}`}
                                        />
                                    </div>
                                </TableCell>
                                <TableCell className={`text-[10px] text-center ${i.id === currentProdIID ? "font-semibold text-salmon" : "font-normal border-ebony"}`}>
                                    {i.ProdProperties[0]?.Attribute.attribute_name}
                                </TableCell>
                                <TableCell className={`text-[12px] text-center ${i.id === currentProdIID ? "font-semibold text-ebony" : "font-normal border-salmon"}`}>
                                    {i.Warehouses[0]?.warehouse_available && i.Warehouses[0]?.warehouse_count > 0 ? i.Warehouses[0]?.warehouse_discount === 0 ? (
                                        <PriceShow prices={i.Warehouses[0]?.warehouse_price} />
                                    ) : (
                                        <DiscountShow discount={i.Warehouses[0]?.warehouse_discount} price={i.Warehouses[0]?.warehouse_price} />
                                    ) : <p className="font-bold text-ebony text-lg">ناموجود</p>}
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default AttributePrices;
